/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  styled,
  Typography,
  IconButton,
  Checkbox,
  Drawer,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import {
  downloadFile,
  getUploadFilters,
  getUploadInfo,
  triggerSqlToAdfPipeline,
  uploadFile,
  getPipelineRunFilters,
} from "../api/ltcpBackend";
import { LTCPContext } from "../LTCPContext";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import ltcpRoutes from "LTCP Module/constants/ltcpRoutes";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StatusCard from "../components/StatusCard";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";


const TILE_VALUE = { name: "", time: "" };

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ tooltip: "LTCPUploadCustomTooltip", popper: className }}
  />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
});

// Hidden input field that we will use for upload button
const VisuallyHiddenInput = styled("input")({
  left: 0,
  bottom: 0,
  width: 1,
  height: 1,
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
});

// This is a card component that will show the given upload file meta data
// and will help the user to upload and validate new data or download the
// existing data.
function UploadTile({
  filename,
  document,
  name,
  time,
  showSuccess,
  productTypeState,
}) {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const { setLoading } = useContext(LTCPContext);
  const [errorDialog, setErrorDialog] = useState(null);
  async function uploadFileApi(filename, file) {
    if (accounts.length < 1) return navigate("/");

    try {
      if (
        [
          "manual_demand_1",
          "manual_demand_2",
          "sourcing_matrix",
          "capacity_master",
          "alert_master",
        ].includes(filename) &&
        !productTypeState
      ) {
        return alert("Please select product type first!");
      }

      setLoading(true);

      const resp = await uploadFile(
        filename,
        file,
        productTypeState,
        activeAccount.username
      );

      setLoading(false);

      if (resp.status === 200) {
        showSuccess();
      } else if (resp.status === 400) {
        const msg = await resp.json();

        if (msg.code) {
          if (msg.code === "REQUEST") {
            alert(msg.msg);
            window.location.reload();
            return;
          } else if (msg.code === "COLUMN") return setErrorDialog(msg);
          else if (msg.code === "INVALID") return setErrorDialog(msg);
        } else {
          alert("Something went wrong! Please try again.");
          window.location.reload();
          return;
        }
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }

  return (
    <Paper>
      <Stack direction="row" alignItems="center">
        <Box px={2} py={0.5} width="20%" sx={{ borderRight: "1px solid #eee" }}>
          <Typography fontSize={11}>{document}</Typography>
        </Box>
        <Box px={2} py={0.5} width="20%" sx={{ borderRight: "1px solid #eee" }}>
          <Button
            component="label"
            role={undefined}
            variant="text"
            tabIndex={-1}
            sx={{ fontSize: 11 }}
            startIcon={<UploadIcon />}
          >
            Upload files
            <VisuallyHiddenInput
              type="file"
              onChange={(e) =>
                e.target.files.length > 0 &&
                uploadFileApi(filename, e.target.files[0])
              }
            />
          </Button>
        </Box>
        <Box px={2} py={0.5} width="20%" sx={{ borderRight: "1px solid #eee" }}>
          <Button
            component="label"
            role={undefined}
            variant="text"
            tabIndex={-1}
            sx={{ fontSize: 11 }}
            startIcon={<DownloadIcon />}
            onClick={() =>
              name && name !== "Not uploaded yet"
                ? downloadFile(filename, productTypeState)
                : alert("File is not present for download")
            }
          >
            Download files
          </Button>
        </Box>
        <Box px={2} py={0.5} width="20%" sx={{ borderRight: "1px solid #eee" }}>
          <Typography fontSize={11}>{name}</Typography>
        </Box>
        <Box px={2} py={0.5} width="20%">
          <Typography fontSize={11}>
            {time ? new Date(time).toLocaleString() : "Updating..."}
          </Typography>
        </Box>
      </Stack>
      <Dialog
        open={Boolean(errorDialog)}
        onClose={() => {
          setErrorDialog(null);
          window.location.reload();
        }}
      >
        <DialogTitle>Validation Failed!</DialogTitle>
        <DialogContent>
          {errorDialog?.unknown &&
            errorDialog.unknown.length > 0 && (
              <Stack>
                <Typography fontSize={14} fontWeight="bold">
                  Factory Code Not Found in Data Lake:
                </Typography>
                <Stack sx={{ height: 200, maxHeight: 200, overflowY: "auto" }}>
                  {errorDialog.unknown.map((item, index) => (
                    <Typography
                      fontSize={12}
                      sx={{ color: "#888" }}
                      key={index * 2}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            )}
          {errorDialog?.extra && errorDialog.extra.length > 0 && (
            <Stack>
              <Typography fontSize={14} fontWeight="bold">
                Extra Columns:
              </Typography>
              <Stack sx={{ height: 200, maxHeight: 200, overflowY: "auto" }}>
                {errorDialog.extra.map((item, index) => (
                  <Typography fontSize={12} sx={{ color: "#888" }} key={index * 2}>
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          )}
          {errorDialog?.missing &&
            errorDialog.missing.length > 0 && (
              <Stack>
                <Typography fontSize={14} fontWeight="bold">
                  Missing Columns:
                </Typography>
                <Stack sx={{ height: 200, maxHeight: 200, overflowY: "auto" }}>
                  {errorDialog.missing.map((item, index) => (
                    <Typography
                      fontSize={12}
                      sx={{ color: "#888" }}
                      key={index * 2}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

// This is the upload page that will show all the uplaod tile. After loading
// this page it will fetch the meta data of each file and will pass it to
// each upload tile.
function UploadPage() {
  const navigate = useNavigate();
  const { setLoading, setAlertWidget, loadPipelineStatusFilter } =
    useContext(LTCPContext);
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;

  const [manualDemand1Tile, setManualDemand1Tile] = useState(TILE_VALUE);
  const [manualDemand2Tile, setManualDemand2Tile] = useState(TILE_VALUE);
  const [sourcingMatrix, setSourcingMatrix] = useState(TILE_VALUE);
  const [capacityMasterTile, setCapacityMasterTile] = useState(TILE_VALUE);
  const [productMasterTile, setProductMasterTile] = useState(TILE_VALUE);
  const [depotMasterTile, setDepotMasterTile] = useState(TILE_VALUE);
  const [factoryMasterTile, setFactoryMasterTile] = useState(TILE_VALUE);
  const [alertMasterTile, setAlertMasterTile] = useState(TILE_VALUE);
  const [branchMasterTile, setBranchMasterTile] = useState(TILE_VALUE);
  const [triggerStatus, setTriggerStatus] = useState(0);
  const [productTypes, setProductTypes] = useState([]);
  const [productTypeState, setProductTypeState] = useState(() => {
    return localStorage.getItem("productType") || null;
  });
  const [runType, setRunType] = useState("");
  const [runPopup, setRunPopup] = useState(false);
  const [manualone, setManualone] = useState(false);
  const [manualtwo, setManualtwo] = useState(false);
  const [statusDrawer, setStatusDrawer] = useState(false);
  const [pipelinefilterFlag, setPipelinefilterFlag] = useState(false);

  async function fetchData() {
    try {
      setLoading(true);

      const resp = await Promise.all([
        getUploadInfo(),
        getUploadFilters(userEmail),
        getPipelineRunFilters(userEmail),
      ]);

      const data = resp[0];
      const filters = resp[1];
      const pipeline_run_filters = resp[2];

      loadPipelineStatusFilter(pipeline_run_filters);

      setManualDemand1Tile(data.manual_demand_1);
      setManualDemand2Tile(data.manual_demand_2);
      setSourcingMatrix(data.sourcing_matrix);
      setCapacityMasterTile(data.capacity_master);
      setProductMasterTile(data.product_master);
      setDepotMasterTile(data.depot_master);
      setFactoryMasterTile(data.factory_master);
      setAlertMasterTile(data.alert_master);
      setBranchMasterTile(data.branch_master);

      setProductTypes(filters);
      if (!productTypeState) {
        setProductTypeState(filters[0]);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }

  function successOpenHandler() {
    setAlertWidget({
      open: true,
      success: true,
      message: "File uploaded successfully!",
      onClose: () => window.location.reload(),
    });
  }

  function alertOpenHandler() {
    setAlertWidget({
      open: true,
      success: false,
      message: "Please select either M1 or M2 demand for data check run.",
      onClose: null,
    });
  }

  async function PipelineStatusfiltersReload() {
    setRunPopup(false)
    if (pipelinefilterFlag) {
      try {
        setLoading(true)

        const response = await getPipelineRunFilters(userEmail)
        loadPipelineStatusFilter(response);
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
        setPipelinefilterFlag(false)
      }
    }

  }



  async function triggerPipeline(isDataCheck) {
    setRunType(isDataCheck ? "DATA_CHECK" : "RUN_NOW");

    setLoading(true);
    try {
      const sts = await triggerSqlToAdfPipeline({
        manualone,
        manualtwo,
        producttype: productTypeState,
        user_email: userEmail,
        isDataCheck,
      });

      setTriggerStatus(sts ? 1 : 2);
    } catch (err) {
      console.error(err);
      setTriggerStatus(2);
    }

    setLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    localStorage.setItem("productType", productTypeState);
  }, [productTypeState]);

  return (
    <Stack p={4} gap={1}>
      <Stack direction="row" alignItems="center">
        <IconButton
          sx={{ color: "black" }}
          onClick={() => navigate(ltcpRoutes.LANDING)}
        >
          <KeyboardBackspaceIcon />
        </IconButton>
        <Typography fontWeight="bold" fontSize={21}>
          Upload Log
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" gap={3}>
        <Box gap={1} display="flex" alignItems="center">
          <Typography fontSize={14}>Product Type</Typography>
          <Select
            size="small"
            sx={{
              px: 2,
              py: 0,
              height: 32,
              width: 180,
              fontSize: 14,
              bgcolor: "#fff",
            }}
            value={productTypeState || ""}
            onChange={(e) => setProductTypeState(e.target.value)}
          >
            {productTypes.map((item, index) => (
              <MenuItem key={index * 2} value={item}>
                {item.replaceAll("_", " ")}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={() => setStatusDrawer(true)}
          sx={{ mb: 4 }}
        >
          RUN STATUS
        </Button>
      </Stack>
      <Stack spacing={1}>
        <UploadTile
          filename="manual_demand_1"
          document="Manual Demand 1"
          name={manualDemand1Tile.name}
          time={manualDemand1Tile.time}
          showSuccess={successOpenHandler}
          productTypeState={productTypeState}
        />
        <UploadTile
          filename="manual_demand_2"
          document="Manual Demand 2"
          name={manualDemand2Tile.name}
          time={manualDemand2Tile.time}
          showSuccess={successOpenHandler}
          productTypeState={productTypeState}
        />
        <UploadTile
          filename="sourcing_matrix"
          document="Sourcing Matrix"
          name={sourcingMatrix.name}
          time={sourcingMatrix.time}
          showSuccess={successOpenHandler}
          productTypeState={productTypeState}
        />
        <UploadTile
          filename="capacity_master"
          document="Capacity Master"
          name={capacityMasterTile.name}
          time={capacityMasterTile.time}
          showSuccess={successOpenHandler}
          productTypeState={productTypeState}
        />
        <UploadTile
          filename="product_master"
          document="Product Master"
          name={productMasterTile.name}
          time={productMasterTile.time}
          showSuccess={successOpenHandler}
        />
        <UploadTile
          filename="depot_master"
          document="Depot Master"
          name={depotMasterTile.name}
          time={depotMasterTile.time}
          showSuccess={successOpenHandler}
        />
        <UploadTile
          filename="factory_master"
          document="Factory Master"
          name={factoryMasterTile.name}
          time={factoryMasterTile.time}
          showSuccess={successOpenHandler}
        />
        <UploadTile
          filename="alert_master"
          document="Alert & Recommendation Master"
          name={alertMasterTile.name}
          time={alertMasterTile.time}
          showSuccess={successOpenHandler}
          productTypeState={productTypeState}
        />
        <UploadTile
          filename="branch_master"
          document="Branch & Cluster Mapping Master"
          name={branchMasterTile.name}
          time={branchMasterTile.time}
          showSuccess={successOpenHandler}
        />
      </Stack>
      <Stack direction="row" justifyContent="end">
        <Button
          variant="contained"
          onClick={() => setRunPopup(true)}
          size="small"
        >
          Run Now
        </Button>
      </Stack>
      <Dialog
        open={triggerStatus > 0}
        onClose={() => {
          if (triggerStatus === 1)
            setPipelinefilterFlag(true)
          setTriggerStatus(0)
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background:
              triggerStatus === 1
                ? "linear-gradient(to bottom, #ecffec, #fff)"
                : "linear-gradient(to bottom, #ffd5d5, #fff)",
            color: "#000",
          },
        }}
      >
        <DialogTitle sx={{ color: "#000" }}>
          {triggerStatus === 1 ? "In Progress" : "Failed"}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} direction="row">
            {triggerStatus === 1 ? (
              <Box
                sx={{
                  mt: 2,
                  width: 48,
                  minWidth: 48,
                  maxWidth: 48,
                  height: 48,
                  minHeight: 48,
                  maxHeight: 48,
                  background: "#fff",
                  borderRadius: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#0f0a",
                  boxShadow: "0px 4px 12px 8px #0001",
                }}
              >
                <CheckCircleIcon />
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 2,
                  width: 48,
                  minWidth: 48,
                  maxWidth: 48,
                  height: 48,
                  minHeight: 48,
                  maxHeight: 48,
                  background: "#fff",
                  borderRadius: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#f00a",
                  boxShadow: "0px 4px 12px 8px #0001",
                }}
              >
                <WarningAmberIcon />
              </Box>
            )}
            <Stack gap={1}>
              <Typography color="#000">
                <b>
                  {triggerStatus === 1 &&
                    (runType === "DATA_CHECK"
                      ? "Data Validation is in Progress"
                      : "Run is in Progress")}
                </b>
              </Typography>
              <Typography color="#000" size="small">
                {triggerStatus === 1
                  ? runType === "DATA_CHECK"
                    ? "It will take few a minutes to reflect , please check the RUN STATUS after some time."
                    : "Please check RUN STATUS for updates."
                  : "Something went wrong! Please refresh the page and try again. If the issue persists, contact the administrator."}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog open={runPopup} onClose={() => PipelineStatusfiltersReload()}>
        <DialogTitle>Trigger Manual Run</DialogTitle>
        <DialogContent>
          <Stack>
            <Box gap={1} display="flex" alignItems="center">
              <Typography fontSize={14}>Product Type</Typography>
              <Select
                size="small"
                sx={{
                  px: 2,
                  py: 0,
                  height: 32,
                  width: 180,
                  fontSize: 14,
                  bgcolor: "#fff",
                }}
                value={productTypeState || ""}
                onChange={(e) => setProductTypeState(e.target.value)}
              >
                {productTypes.map((item, index) => (
                  <MenuItem key={index * 2} value={item}>
                    {item.replaceAll("_", " ")}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box
              gap={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography fontSize={14}>V1 Demand</Typography>
              <Checkbox disabled checked size="small" />
            </Box>
            <Box
              gap={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography fontSize={14}>M1 Demand</Typography>
              <Checkbox
                size="small"
                checked={manualone}
                onChange={() => setManualone(!manualone)}
              />
            </Box>
            <Box
              gap={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography fontSize={14}>M2 Demand</Typography>
              <Checkbox
                size="small"
                checked={manualtwo}
                onChange={() => setManualtwo(!manualtwo)}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          {/* Top Buttons */}
          <Box
            display="flex"
            gap={1}
          >
            <Button
              variant="contained"
              size="small"
              onClick={() => { manualone || manualtwo ? triggerPipeline(true) : alertOpenHandler() }}
            >
              Run with Data Check*
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={() => triggerPipeline(false)}
            >
              Run Now
            </Button>
          </Box>

          {/* Warning Message */}
          <Typography
            variant="caption"
            sx={{
              color: "red",
              fontWeight: 500,
              alignSelf: "flex-start",
            }}
          >
            * Data Check will take few mins. to complete and reflect if failed.
          </Typography>
        </DialogActions>
      </Dialog>
      <Drawer
        open={statusDrawer}
        onClose={() => setStatusDrawer(false)}
        anchor="right"
      >
        <>
          <Stack
            pl={2}
            height={48}
            bgcolor="#1F36C7"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography color="#fff" fontWeight="bold">
                Run Status
              </Typography>
              <CustomWidthTooltip
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -14],
                        },
                      },
                    ],
                  },
                }}
                classes={{ tooltip: "LTCPUploadCustomTooltip" }}
                title="After clicking on Run Now, the pipeline status will update approximately after 2.5 hours and display one of the following: Run Successful, Failed, or No Run.
In case of Failed: The issue may have occurred due to a pipeline failure. Please raise a Service Request (SR). The BAU team will investigate, resolve the issue, and provide a Root Cause Analysis (RCA)."
              >
                <IconButton>
                  <InfoOutlineIcon sx={{ color: "#fff" }} />
                </IconButton>
              </CustomWidthTooltip>
            </Box>
            <IconButton onClick={() => setStatusDrawer(false)}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Stack>
          <Stack alignItems="stretch" p={2} pt={4} gap={4}>
            <StatusCard runType="Manual" userEmail={userEmail} />
            <StatusCard runType="Schedule" userEmail={userEmail} />
          </Stack>
        </>
      </Drawer>
    </Stack>
  );
}

UploadTile.propTypes = {
  filename: PropTypes.string.isRequired,
  document: PropTypes.string.isRequired,
  name: PropTypes.string,
  time: PropTypes.string,
  showSuccess: PropTypes.func.isRequired,
  productTypeState: PropTypes.string,
};

export default UploadPage;
